import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ArrowsClockwise, X, Robot } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import ReactMarkdown from 'react-markdown';

// Markdown component definitions - moved outside to fix React lint warning
const MarkdownComponents = {
  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-[#2C4C3B] mb-3 mt-4" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-xl font-semibold text-[#2C4C3B] mb-2 mt-3" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-lg font-medium text-[#2C4C3B] mb-2 mt-2" {...props} />,
  p: ({ node, ...props }) => <p className="text-gray-700 mb-3 leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-[#2C4C3B]" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="text-gray-700 ml-2" {...props} />,
};

const LANGUAGES = [
  { code: 'ta', name: 'தமிழ்', flag: '🇮🇳', label: 'Tamil' },
  { code: 'en', name: 'English', flag: '🇮🇳', label: 'English' },
  { code: 'kn', name: 'ಕನ್ನಡ', flag: '🇮🇳', label: 'Kannada' },
  { code: 'te', name: 'తెలుగు', flag: '🇮🇳', label: 'Telugu' },
  { code: 'ml', name: 'മലയാളം', flag: '🇮🇳', label: 'Malayalam' }
];

const AIGuideDrawer = ({ isOpen, onClose, customerId, customerName }) => {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Load saved language preference from localStorage, default to Tamil
    return localStorage.getItem('aiGuideLanguage') || 'ta';
  });

  useEffect(() => {
    if (isOpen && customerId) {
      fetchGuide();
    }
  }, [isOpen, customerId]);

  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('aiGuideLanguage', langCode);
    
    // Auto-regenerate guide if one already exists
    if (guide?.guide_content && !guide?.error) {
      setRegenerating(true);
      try {
        const response = await api.post(`/ai-guides/regenerate/${customerId}`, {
          language: langCode
        });
        setGuide(response.data);
        toast.success(`AI Guide regenerated in ${LANGUAGES.find(l => l.code === langCode)?.label}!`);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to regenerate guide'));
      } finally {
        setRegenerating(false);
      }
    }
  };

  const fetchGuide = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/ai-guides/${customerId}`);
      setGuide(response.data);
    } catch (error) {
      console.error('Failed to fetch AI guide:', error);
      setGuide(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await api.post(`/ai-guides/regenerate/${customerId}`, {
        language: selectedLanguage
      });
      setGuide(response.data);
      toast.success('AI Guide regenerated successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to regenerate guide'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerate = async () => {
    setRegenerating(true);
    try {
      const response = await api.post(`/ai-guides/regenerate/${customerId}`, {
        language: selectedLanguage
      });
      setGuide(response.data);
      toast.success('AI Guide generated successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate guide'));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-[#2C4C3B] flex items-center gap-2">
              <Robot size={24} weight="fill" className="text-teal-500" />
              AI Guide - {customerName}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {guide?.guide_content && !guide?.error && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="text-teal-600 border-teal-600 hover:bg-teal-50"
                >
                  <ArrowsClockwise size={16} className={regenerating ? 'animate-spin' : ''} />
                  <span className="ml-2">Refresh</span>
                </Button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Language Selector */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">Select Language:</div>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  disabled={regenerating}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedLanguage === lang.code
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-1">{lang.flag}</span>
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-teal-500 border-r-transparent"></div>
                <p className="mt-4 text-gray-600">Loading AI Guide...</p>
              </div>
            </div>
          ) : guide?.error ? (
            <div className="text-center py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-700 font-medium mb-2">Error generating guide</p>
                <p className="text-sm text-red-600 mb-4">{guide.error}</p>
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  <ArrowsClockwise size={16} className={regenerating ? 'animate-spin mr-2' : 'mr-2'} />
                  Try Again
                </Button>
              </div>
            </div>
          ) : guide?.guide_content ? (
            <div className="prose prose-sm max-w-none">
              <div className="bg-teal-50 border-l-4 border-teal-500 p-4 mb-4 rounded">
                <p className="text-xs text-teal-800">
                  Generated: {new Date(guide.generated_at).toLocaleString()}
                </p>
              </div>
              <ReactMarkdown components={MarkdownComponents}>
                {guide.guide_content}
              </ReactMarkdown>
            </div>
          ) : guide?.message ? (
            <div className="text-center py-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <Robot size={48} className="mx-auto mb-4 text-yellow-600" />
                <p className="text-yellow-800 font-medium mb-2">No AI Guide Available</p>
                <p className="text-sm text-yellow-700 mb-4">
                  {guide.message}
                </p>
                <Button
                  onClick={handleGenerate}
                  disabled={regenerating}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  <Robot size={16} className={regenerating ? 'animate-spin mr-2' : 'mr-2'} />
                  Generate Guide Now
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <p className="text-gray-600 mb-4">No guide available</p>
                <Button
                  onClick={handleGenerate}
                  disabled={regenerating}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  Generate Guide
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIGuideDrawer;
